import { useState, useEffect } from 'react'

export interface FoldableState {
  /** True if the viewport is spanned across two horizontal segments (dual screen / book fold) */
  isSpanned: boolean
  /** True if the viewport is spanned across two vertical segments (clamshell fold / tabletop) */
  isVerticalSpanned: boolean
  /** True if device is half-folded in tabletop / flex mode */
  isTabletop: boolean
  /** True if device is an unfolded foldable (aspect ratio 0.7-1.4, width 600-1023px) */
  isFoldableUnfolded: boolean
  /** True if foldable dual-pane (chat + dashboard side-by-side) should be active */
  isDualPane: boolean
  /** True if any foldable feature is active */
  isFoldable: boolean
  /** Device posture if supported by Device Posture API ('continuous' | 'folded' | 'none') */
  posture: 'continuous' | 'folded' | 'none'
  /** Current viewport width */
  viewportWidth: number
  /** Current viewport height */
  viewportHeight: number
}

const DEFAULT_STATE: FoldableState = {
  isSpanned: false,
  isVerticalSpanned: false,
  isTabletop: false,
  isFoldableUnfolded: false,
  isDualPane: false,
  isFoldable: false,
  posture: 'none',
  viewportWidth: 0,
  viewportHeight: 0,
}

function queryMedia(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  try {
    return window.matchMedia(query).matches
  } catch {
    return false
  }
}

export function computeFoldableState(): FoldableState {
  if (typeof window === 'undefined') {
    return DEFAULT_STATE
  }

  const width = window.innerWidth
  const height = window.innerHeight
  const aspectRatio = width / (height || 1)

  // Standard CSS Viewport Segments Level 5 & Legacy screen-spanning
  const isHorizontalSpanned =
    queryMedia('(horizontal-viewport-segments: 2)') ||
    queryMedia('(screen-spanning: single-fold-vertical)')

  const isVertSpanned =
    queryMedia('(vertical-viewport-segments: 2)') ||
    queryMedia('(screen-spanning: single-fold-horizontal)')

  // Device Posture API
  type DevicePosture = { type?: 'continuous' | 'folded' }
  const nav = navigator as unknown as { devicePosture?: DevicePosture }
  const posture: 'continuous' | 'folded' | 'none' =
    nav.devicePosture?.type === 'folded'
      ? 'folded'
      : nav.devicePosture?.type === 'continuous'
        ? 'continuous'
        : 'none'

  // Tabletop mode (e.g. clamshell fold with horizontal crease)
  const isTabletop = isVertSpanned

  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
  const isTablet =
    /iPad|tablet/i.test(userAgent) ||
    (/Macintosh/i.test(userAgent) &&
      typeof navigator !== 'undefined' &&
      navigator.maxTouchPoints > 1 &&
      !/SM-F|Fold|Surface Duo/i.test(userAgent))

  // Allow URL override for testing: ?foldable=true or ?fold=1
  const isExplicitFoldable =
    typeof window !== 'undefined' &&
    /[?&](foldable=true|fold=1|view=foldable)/i.test(window.location.search)

  // Explicit foldable hardware identification (Samsung Galaxy Z Fold, Pixel Fold, Surface Duo, etc.)
  const isFoldableHardware =
    /SM-F\d|Pixel Fold|Surface Duo|HMA-|OXF-|V2178A|V2229A/i.test(userAgent)

  // Unfolded single-canvas foldable inner display:
  // Foldable inner screens (Galaxy Z Fold, Pixel Fold, OnePlus Open) have:
  // - In portrait: width 600-1023px, aspectRatio 0.70-1.42
  // - In landscape: width 900-1220px, height 600-1024px, aspectRatio 1.15-1.45
  // Standard phones in portrait have aspect ratio > 1.7 (e.g. 9:19.5 ~ 2.16)
  // When folded, the cover screen has aspect ratio > 2.0 and width < 420px.
  // Standard desktop has aspectRatio >= 1.6 (16:9, 16:10).
  // Standard tablets (iPad, Galaxy Tab) are excluded unless dual-screen or folded posture is reported.
  const isFoldablePortrait =
    !isTablet &&
    width >= 600 &&
    width < 1024 &&
    aspectRatio >= 0.70 &&
    aspectRatio <= 1.42

  const isFoldableLandscape =
    !isTablet &&
    width >= 900 &&
    width <= 1220 &&
    height >= 600 &&
    height <= 1024 &&
    aspectRatio >= 1.15 &&
    aspectRatio <= 1.45

  const isFoldableUnfolded =
    isExplicitFoldable ||
    isFoldableHardware ||
    isFoldablePortrait ||
    isFoldableLandscape

  const isSpanned = isHorizontalSpanned
  const isDualPane = isSpanned || isFoldableUnfolded
  const isFoldable = isSpanned || isVertSpanned || isFoldableUnfolded || isTabletop

  return {
    isSpanned,
    isVerticalSpanned: isVertSpanned,
    isTabletop,
    isFoldableUnfolded,
    isDualPane,
    isFoldable,
    posture,
    viewportWidth: width,
    viewportHeight: height,
  }
}

/**
 * Hook to detect foldable devices, dual-screen spanning, and inner display postures.
 * Preserves 100% standard mobile (<600px) and desktop (>=1024px) layouts while enabling
 * tailored dual-pane and tabletop modes on foldable hardware.
 */
export function useFoldable(): FoldableState {
  const [state, setState] = useState<FoldableState>(() => computeFoldableState())

  useEffect(() => {
    let rafId: number | null = null

    const update = () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        setState(computeFoldableState())
      })
    }

    // Media query matchers
    const mqlHorizontal1 = window.matchMedia('(horizontal-viewport-segments: 2)')
    const mqlHorizontal2 = window.matchMedia('(screen-spanning: single-fold-vertical)')
    const mqlVertical1 = window.matchMedia('(vertical-viewport-segments: 2)')
    const mqlVertical2 = window.matchMedia('(screen-spanning: single-fold-horizontal)')

    const addMqlListener = (mql: MediaQueryList) => {
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', update)
      } else if (typeof mql.addListener === 'function') {
        mql.addListener(update)
      }
    }

    const removeMqlListener = (mql: MediaQueryList) => {
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', update)
      } else if (typeof mql.removeListener === 'function') {
        mql.removeListener(update)
      }
    }

    addMqlListener(mqlHorizontal1)
    addMqlListener(mqlHorizontal2)
    addMqlListener(mqlVertical1)
    addMqlListener(mqlVertical2)

    window.addEventListener('resize', update, { passive: true })
    window.addEventListener('orientationchange', update, { passive: true })

    // Device Posture API listener if available
    type DevicePostureWithEvents = {
      addEventListener?: (type: string, listener: () => void) => void
      removeEventListener?: (type: string, listener: () => void) => void
    }
    const nav = navigator as unknown as { devicePosture?: DevicePostureWithEvents }
    nav.devicePosture?.addEventListener?.('change', update)

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      removeMqlListener(mqlHorizontal1)
      removeMqlListener(mqlHorizontal2)
      removeMqlListener(mqlVertical1)
      removeMqlListener(mqlVertical2)
      window.removeEventListener('resize', update)
      window.removeEventListener('orientationchange', update)
      nav.devicePosture?.removeEventListener?.('change', update)
    }
  }, [])

  return state
}
